import Question from "./Question";
import ModuleWrapper from "./ModuleWrapper";
import log from "loglevel";

const QuestionsList = ({ otherActivityData, questionRefs, activity_evaluated, labId, setOtherActivityData, isDisabled, labAvailableTime, showMarksToStudent, revealAnswers, markDirty }) => {

    const seenQuestions = new Set();

    const updateResponse = (updateresp) => {
        const updatedActivityData = {
            ...otherActivityData,
            question_set: otherActivityData.question_set.map(question => {
                if (question?.id === updateresp?.id) {
                    return {
                        ...question,
                        questionresponse_set: updateresp
                    };
                }
                return question;
            })
        };

        // Update the state with the updated activity data
        setOtherActivityData(updatedActivityData);
        // Mark quiz dirty so the autosave timer in student/index.jsx fires
        // on its next tick. Optional prop — safe no-op if parent doesn't pass it.
        if (typeof markDirty === "function") {
            markDirty();
        }
    };

    // Get modules if they exist
    const modules = otherActivityData?.questionmodule_set || [];

    // Separate standalone questions (not in any module)
    const allQuestions = otherActivityData?.question_set || [];
    const standaloneQuestions = allQuestions.filter(q => !q.module);

    // Group questions by module
    const modulesWithQuestions = modules.map(module => ({
        ...module,
        question_set: allQuestions.filter(q => q.module === module.id)
    }));

    // Create unified list combining modules and standalone questions
    const unifiedList = [
        // Add modules with type indicator
        ...modulesWithQuestions.map(module => ({
            ...module,
            itemType: 'module',
            order: module.order ?? 0
        })),
        // Add standalone questions with type indicator
        ...standaloneQuestions.map(question => ({
            ...question,
            itemType: 'question',
            order: question.order ?? 0
        }))
    ].sort((a, b) => {
        // Primary sort: by order value
        const orderDiff = (a.order ?? 0) - (b.order ?? 0);
        if (orderDiff !== 0) return orderDiff;

        // Secondary sort when orders are equal: questions before modules
        if (a.itemType !== b.itemType) {
            return a.itemType === 'question' ? -1 : 1;
        }

        // Tertiary sort: by ID
        return a.id - b.id;
    });

    // Track question index for continuous numbering
    let globalQuestionIndex = 0;

    const renderQuestion = (question, localIndex, isInModule = false) => {
        // Filter duplicates using unique id instead of question_id
        if (seenQuestions.has(question.id)) {
            return null;
        }
        seenQuestions.add(question.id);

        const currentIndex = globalQuestionIndex;
        globalQuestionIndex++;

        try {
            return (
                <div className="my-3" key={question.id}>
                    <Question
                        question={question}
                        index={currentIndex}
                        labId={labId}
                        previousResponse={question?.questionresponse_set || { id: question?.id, response_text: "", response_options: [], response_options_ids: [] }}
                        updateResponse={updateResponse}
                        labAvailableTime={labAvailableTime}
                        dis={isDisabled || false}
                        Eval={activity_evaluated || false}
                        ref={(el) => (questionRefs.current[currentIndex] = el)}
                        isDisabled={isDisabled}
                        showMarksToStudent={showMarksToStudent}
                        revealAnswers={revealAnswers}
                    />
                </div>
            );
        } catch (error) {
            log.error("Error occurred:", error);
            return (
                <div className="my-3" key={question.id}>
                    <p>Error occurred while rendering question.</p>
                </div>
            );
        }
    };

    const renderModule = (module, index) => {
        const moduleQuestions = module.question_set || [];

        return (
            <ModuleWrapper key={`module-${module.id}`} module={module} startIndex={globalQuestionIndex}>
                {moduleQuestions.map((question, localIndex) =>
                    renderQuestion(question, localIndex, true)
                )}
            </ModuleWrapper>
        );
    };

    return (<>
        {unifiedList.length > 0 ? (
            unifiedList.map((item, index) => {
                if (item.itemType === 'module') {
                    return renderModule(item, index);
                } else if (item.itemType === 'question') {
                    return renderQuestion(item, index, false);
                }
                return null;
            })
        ) : (
            // Fallback for backwards compatibility - render all questions if no unified list
            allQuestions.filter(question => {
                const isDuplicate = seenQuestions.has(question.id);
                seenQuestions.add(question.id);
                return !isDuplicate;
            }).map((question, index) => renderQuestion(question, index, false))
        )}
    </>);
};

export default QuestionsList;